<template>
  <div class="callback-req-card">
    <div class="callback-req-card__header">
      <span class="callback-req-card__name">{{ cardDynamicData.buyer_full_name }}</span>
      <a class="callback-req-card__phone" :href="`tel:${buyerTelNumber}`">{{ buyerTelNumber }}</a>
    </div>
    <div class="callback-req-card__badges">
      <span v-if="financingStatus" class="callback-req-card__badge">{{ financingStatus }}</span>
      <span class="callback-req-card__badge">{{ buyingPurpose }}</span>
      <span class="callback-req-card__badge" :class="financialViabilityOutcomeClassName">
        {{ $t('DASHBOARD.CARD.EXPANDED.CALLBACK.BUDGET') }}
      </span>
      <span
        v-if="cardDynamicData.my_homeday_access"
        class="callback-req-card__badge callback-req-card__badge--primary"
        >{{ $t('DASHBOARD.CARD.EXPANDED.CONTACT.MY_HOMEDAY') }}</span
      >
    </div>
    <div class="callback-req-card__status">
      <span class="callback-req-card__date">{{ requestedAt }}</span>
      <div v-if="cardDynamicData.snooze_count" class="callback-req-card__counter">
        <HdIcon :src="icons.outgoingCall" height="16" width="16" />
        <span>({{ cardDynamicData.snooze_count }})</span>
        <HdIcon :src="icons.notificationsActive" height="16" width="16" />
        <span :title="snoozeUntil.formattedDate">{{ snoozeUntil.time }}</span>
      </div>
    </div>
    <div class="callback-req-card__actions">
      <button
        class="callback-req-card__action"
        @click="actionHandler(ACTIONS.COMPLETE_CALLBACK_REQUEST)"
      >
        {{ $t('DASHBOARD.CARD.EXPANDED.CALLBACK.ACTION.STATUS_PROMPT') }}
        <span
          v-if="timeUntilDue"
          class="callback-req-card__badge callback-req-card__badge--secondary"
          v-text="
            $t('DASHBOARD.CARD.EXPANDED.CALLBACK.ACTION.BADGE.TIME_UNTIL_DUE', {
              value: timeUntilDue,
            })
          "
        />
        <span
          v-if="timeSinceOverdue"
          class="callback-req-card__badge"
          v-text="
            $t('DASHBOARD.CARD.EXPANDED.CALLBACK.ACTION.BADGE.TIME_SINCE_OVERDUE', {
              value: timeSinceOverdue,
            })
          "
        />
      </button>
      <button
        v-if="canSnoozeCallback"
        class="callback-req-card__action callback-req-card__action--secondary"
        @click="actionHandler(ACTIONS.SNOOZE_CALLBACK_REQUEST)"
      >
        {{ $t('DASHBOARD.CARD.EXPANDED.CALLBACK.ACTION.SNOOZE_PROMPT') }}
      </button>
    </div>
  </div>
</template>

<script>
import { HdIcon } from 'homeday-blocks';
import { outgoingCall, notificationsActive, time, arrow } from 'homeday-assets';
import { FINANCING_STATUS_TYPES, BUYING_PURPOSE } from '@/config/TRANSLATION_CONSTANTS';
import dashboardCardMixin from '@/mixins/dashboardCardMixin';

export default {
  name: 'CallbackRequestCard',
  components: { HdIcon },
  mixins: [dashboardCardMixin],
  props: {
    deal: {
      type: Object,
      required: true,
    },
    cardDynamicData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    icons() {
      return {
        outgoingCall,
        notificationsActive,
        time,
        arrow,
      };
    },
    financingStatus() {
      return FINANCING_STATUS_TYPES[this.cardDynamicData.financing_status?.toUpperCase()];
    },
    buyingPurpose() {
      return BUYING_PURPOSE[this.cardDynamicData.buying_purpose.toUpperCase()];
    },
    financialViabilityOutcomeClassName() {
      return `callback-req-card__badge--${this.cardDynamicData.financial_viability_outcome}`;
    },
    snoozeUntil() {
      const formattedDate = new Date(this.cardDynamicData.snooze_until);

      const options = {
        weekday: 'short',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      };

      const [weekday, date, time] = new Intl.DateTimeFormat('de-DE', options)
        .format(formattedDate)
        .split(',');

      return {
        time,
        formattedDate: this.$t('DASHBOARD.CARD.EXPANDED.CALLBACK.POSTPONED_UNTIL', {
          weekday,
          date,
          time,
        }),
      };
    },
    timeUntilDue() {
      const CALLBACK_DUE_PERIOD = this.deal.callbacks_due_period || 72;
      const timeUntilDue = this.cardDynamicData.snooze_until || this.cardDynamicData.created_at;

      if (!timeUntilDue) {
        return 0;
      }

      const dueDate = new Date(timeUntilDue);
      const currentDate = new Date();
      const remainingHours = Number(Math.ceil((dueDate - currentDate) / (1000 * 60 * 60)));

      if (remainingHours <= 0 || remainingHours > CALLBACK_DUE_PERIOD) {
        return 0;
      }

      return remainingHours;
    },
    timeSinceOverdue() {
      const CALLBACK_OVERDUE_PERIOD = this.deal.callbacks_overdue_period || 96;
      const timeSinceOverdue = this.cardDynamicData.overdue_at;

      if (!timeSinceOverdue) {
        return 0;
      }

      const overdueDate = new Date(timeSinceOverdue);
      const currentDate = new Date();
      const HoursSinceOverdue = Number(Math.ceil((currentDate - overdueDate) / (1000 * 60 * 60)));

      if (HoursSinceOverdue <= 0 || HoursSinceOverdue > CALLBACK_OVERDUE_PERIOD) {
        return 0;
      }

      return HoursSinceOverdue;
    },
    requestedAt() {
      const formattedDate = new Date(this.cardDynamicData.created_at);

      const options = {
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      };

      const [weekday, date, time] = new Intl.DateTimeFormat('de-DE', options)
        .format(formattedDate)
        .split(',');

      return this.$t('DASHBOARD.CARD.EXPANDED.CALLBACK.REQUESTED_AT', {
        weekday,
        date,
        time,
      });
    },
    buyerTelNumber() {
      return this.cardDynamicData.buyer_mobile_number || this.cardDynamicData.buyer_phone_number;
    },
    canSnoozeCallback() {
      const currentTime = new Date();
      const overdueAt = new Date(this.cardDynamicData.overdue_at);
      const snoozeUntil = this.cardDynamicData.snooze_until;

      const isOverdue = overdueAt < currentTime;
      const isSnoozeActive = snoozeUntil && new Date(snoozeUntil) > currentTime;

      return !(isOverdue || isSnoozeActive);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.callback-req-card {
  $root: &;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $sp-s;
  }

  &__name {
    @include fontGroup(100, 600);
    color: $primary-color;
  }

  &__phone {
    @include fontGroup(90, 400);
    color: getShade($secondary-color, 120);
    text-decoration: none;
  }

  &__badges {
    display: flex;
    flex-wrap: wrap;
  }

  &__badge {
    padding: $sp-xxs $sp-s;
    @include fontGroup(80, 400);
    color: $primary-color;
    background-color: getShade($quaternary-color, 50);
    margin-right: $sp-xs;
    margin-bottom: $sp-s;
    border-radius: $sp-s;

    &--likely {
      color: $white;
      background-color: $apple-green;
    }

    &--unlikely {
      color: $white;
      background-color: $tertiary-color;
    }

    &--incomplete_info {
      color: $white;
      background-color: $error-color;
    }

    &--primary {
      color: $white;
      background-color: getShade($secondary-color, 120);
    }
  }

  &__status {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__date {
    @include fontGroup(90, 400);
    color: $primary-color;
  }

  &__counter {
    border-radius: $sp-s;
    margin-right: $sp-s;
    border: 1px solid getShade($quaternary-color, 50);
    padding: $sp-xxs $sp-xs;
    display: flex;
    @include fontGroup(80, 400);
    width: 105px;
    justify-content: space-around;
    margin-left: $sp-s;
  }

  &__actions {
    display: flex;
  }

  &__action {
    position: relative;
    border: none;
    text-align: left;
    border-radius: $sp-xs;
    cursor: pointer;
    padding: $sp-s ($sp-m - $sp-xs);
    background-color: getShade($secondary-color, 120);
    @include fontGroup(90, 400);
    margin-right: $sp-s + $sp-xs;
    margin-top: $sp-s + $sp-xs;
    color: $white;
    height: $sp-l;
    line-height: $sp-m;

    &:hover {
      @include elevation(2);
    }

    &--secondary {
      border: 1px solid getShade($secondary-color, 120);
      color: getShade($secondary-color, 120);
      background-color: $white;
    }

    #{$root}__badge {
      position: absolute;
      margin: 0;
      right: -8px;
      top: -11px;
      background-color: getShade($error-color, 50);
      color: $error-color;
      @include fontGroup(80, 400);
      padding: $sp-xxs ($sp-xxs + $sp-xs);
      border-radius: $sp-xxs + $sp-s;
      height: 20px;

      &--secondary {
        color: $primary-color;
        background-color: getShade($quaternary-color, 50);
      }
    }
  }
}
</style>
